/**
 * Environment-aware logging utility for mySuccession
 * 
 * Automatically suppresses debug/info logs in production while maintaining
 * error/warn visibility for monitoring. Integrates with Sentry for error tracking.
 * 
 * @example
 * ```typescript
 * import { logger } from '@/utils/logger';
 * 
 * logger.debug('Detailed debugging info', data);
 * logger.info('Informational message');
 * logger.warn('Deprecated API used');
 * logger.error('Failed to save data', error);
 * logger.component('UserProfile', 'Mounted with user data', user);
 * ```
 */

import * as Sentry from '@sentry/react';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * Logger class providing environment-aware logging
 * - Development: All logs visible with timestamps
 * - Production: Only warn/error visible
 * - Test: All logs suppressed
 */
class Logger {
  private readonly isDev: boolean;
  private readonly isTest: boolean;
  private readonly isProd: boolean;

  constructor() {
    // Use Vite environment variables
    this.isDev = import.meta.env.DEV;
    this.isTest = import.meta.env.MODE === 'test';
    this.isProd = import.meta.env.PROD;
  }

  /**
   * Check if logging is enabled for the given level
   * @private
   */
  private isEnabled(level: LogLevel): boolean {
    // Suppress all logs in test environment
    if (this.isTest) {
      return false;
    }

    // In production, only allow warn and error
    if (this.isProd) {
      return level === 'warn' || level === 'error';
    }

    // In development, allow all logs
    return true;
  }

  /**
   * Get formatted timestamp for log messages
   * @private
   */
  private getTimestamp(): string {
    return new Date().toISOString();
  }

  /**
   * Safely serialize an object for logging
   * Handles circular references and errors
   * @private
   */
  private serialize(data: any): any {
    try {
      // Handle primitives
      if (data === null || data === undefined) {
        return data;
      }

      // Handle Error objects specially
      if (data instanceof Error) {
        return {
          name: data.name,
          message: data.message,
          stack: this.isDev ? data.stack : undefined, // Stack only in dev
        };
      }

      // Return objects as-is (console will handle them)
      return data;
    } catch (error) {
      return '[Unserializable data]';
    }
  }

  /**
   * Development-only detailed logging
   * Suppressed in production and test environments
   * 
   * @example
   * ```typescript
   * logger.debug('User data loaded', { userId: 123, name: 'John' });
   * ```
   */
  debug(...args: any[]): void {
    if (!this.isEnabled('debug')) {
      return;
    }

    const serialized = args.map(arg => this.serialize(arg));
    console.log('[DEBUG]', this.getTimestamp(), ...serialized);
  }

  /**
   * Informational messages for development
   * Suppressed in production and test environments
   * 
   * @example
   * ```typescript
   * logger.info('Assessment saved successfully');
   * ```
   */
  info(...args: any[]): void {
    if (!this.isEnabled('info')) {
      return;
    }

    const serialized = args.map(arg => this.serialize(arg));
    console.info('[INFO]', this.getTimestamp(), ...serialized);
  }

  /**
   * Warning messages (visible in all environments)
   * Use for deprecated features, potential issues
   * 
   * @example
   * ```typescript
   * logger.warn('API endpoint deprecated, migrate to v2');
   * ```
   */
  warn(...args: any[]): void {
    if (!this.isEnabled('warn')) {
      return;
    }

    const serialized = args.map(arg => this.serialize(arg));
    console.warn('[WARN]', this.getTimestamp(), ...serialized);
  }

  /**
   * Error messages (visible in all environments)
   * Automatically sent to Sentry if available
   * 
   * @example
   * ```typescript
   * logger.error('Failed to save assessment', error, { assessmentId: 123 });
   * ```
   */
  error(...args: any[]): void {
    if (!this.isEnabled('error')) {
      return;
    }

    const serialized = args.map(arg => this.serialize(arg));
    console.error('[ERROR]', this.getTimestamp(), ...serialized);

    // Send to Sentry if first arg is an Error object
    if (args[0] instanceof Error) {
      try {
        // Add context from additional arguments
        if (args.length > 1) {
          Sentry.setContext('error_details', {
            additionalData: args.slice(1),
          });
        }
        Sentry.captureException(args[0]);
      } catch (sentryError) {
        // Sentry might not be initialized, that's okay
        console.error('[Logger] Failed to send error to Sentry:', sentryError);
      }
    }
  }

  /**
   * Convenience method for component lifecycle logging
   * Automatically prefixes with component name
   * Only logs in development mode
   * 
   * @example
   * ```typescript
   * logger.component('UserProfile', 'Component mounted', { userId: 123 });
   * logger.component('Dashboard', 'Data fetched', { recordCount: 45 });
   * ```
   */
  component(componentName: string, message: string, data?: any): void {
    if (!this.isDev) {
      return;
    }

    const prefix = `[${componentName}]`;
    if (data !== undefined) {
      this.debug(prefix, message, data);
    } else {
      this.debug(prefix, message);
    }
  }

  /**
   * Group logs together (development only)
   * Useful for debugging complex operations
   * 
   * @example
   * ```typescript
   * logger.group('Assessment Submission');
   * logger.debug('Validating data...');
   * logger.debug('Saving to database...');
   * logger.groupEnd();
   * ```
   */
  group(label: string): void {
    if (!this.isDev) {
      return;
    }
    console.group(`[GROUP] ${label}`);
  }

  groupEnd(): void {
    if (!this.isDev) {
      return;
    }
    console.groupEnd();
  }

  /**
   * Log a table (development only)
   * Great for debugging arrays of objects
   * 
   * @example
   * ```typescript
   * logger.table(users); // Shows users in table format
   * ```
   */
  table(data: any): void {
    if (!this.isDev) {
      return;
    }
    console.table(data);
  }
}

// Export singleton instance
export const logger = new Logger();

// Export type for advanced usage
export type { Logger };

/**
 * TEST ONLY: Trigger a test error to verify Sentry integration
 * Usage: In browser console, run: window.testLogger()
 */
export function testLogger(): void {
  try {
    console.log('[Logger Test] Testing all log levels...');
    
    logger.debug('Debug message test', { sample: 'data' });
    logger.info('Info message test');
    logger.warn('Warning message test');
    
    // Throw test error
    throw new Error('Test error: Logger integration verification');
  } catch (error) {
    logger.error('Test error captured', error as Error, { context: 'test' });
    console.log('[Logger Test] Check console and Sentry dashboard for results');
  }
}

// Make available in browser console for testing
if (import.meta.env.DEV) {
  (window as any).testLogger = testLogger;
}
